import { useQuery } from '@tanstack/react-query';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BookOpen, Bot, Cpu, Shield, Search, Megaphone, GitBranch, Server, Zap, BrainCircuit, ExternalLink, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ICONS = { 'MOA': BrainCircuit, 'ARIA': Zap, 'CHANGE-IQ': GitBranch, 'ATLAS': Server, 'SENTINL': Shield, 'NOVA': Search, 'CONCIERGE': Bot, 'CAPACITOR': Cpu, 'HERALD': Megaphone, 'LEARN-X': BookOpen };
const COLORS = { 'MOA': '#3B82F6', 'ARIA': '#EF4444', 'CHANGE-IQ': '#F59E0B', 'ATLAS': '#06B6D4', 'SENTINL': '#EF4444', 'NOVA': '#8B5CF6', 'CONCIERGE': '#10B981', 'CAPACITOR': '#F97316', 'HERALD': '#EC4899', 'LEARN-X': '#6366F1' };

export default function DocsPage() {
  const token = localStorage.getItem('nexus_token');
  const [expandedAgent, setExpandedAgent] = useState(null);

  const { data: agentDocs } = useQuery({
    queryKey: ['agent-docs'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/docs/agents`, { headers: { Authorization: `Bearer ${token}` } }); return data; },
  });

  return (
    <div data-testid="docs-page" className="w-full max-w-[1600px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Documentation</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Agent reference, getting started, and platform guides</p>
      </div>

      {/* Quick Start */}
      <div className="nexus-card relative overflow-hidden p-4 mb-4">
        <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-3">Getting Started</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[{title: '1. Trigger a Scenario', desc: 'Go to Settings → Agent Scenarios and trigger SCENARIO_MEMORY_LEAK to see ARIA resolve an incident autonomously.'},
            {title: '2. Watch the Feed', desc: 'Navigate to Incidents and click the newly created ticket to see the ARIA live action feed with 9 nodes executing in real-time.'},
            {title: '3. Explore Analytics', desc: 'Visit Analytics to see KPIs, agent performance, cost avoidance, and use the NLP Query page to ask questions.'}].map((step, i) => (
            <div key={i} className="p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
              <h3 className="text-xs font-semibold text-[var(--nexus-accent)] mb-1">{step.title}</h3>
              <p className="text-[11px] text-[var(--nexus-text-secondary)] leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Agent Reference */}
      <div className="nexus-card relative overflow-hidden">
        <div className="p-3 border-b border-[var(--nexus-border)]">
          <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Agent Reference ({(agentDocs || []).length} agents)</h2>
        </div>
        <ScrollArea className="h-[500px]">
          <div className="p-3 space-y-2" data-testid="agent-docs-list">
            {(agentDocs || []).map(agent => {
              const Icon = ICONS[agent.name] || Bot;
              const color = COLORS[agent.name] || '#06B6D4';
              const isExpanded = expandedAgent === agent.name;
              return (
                <div key={agent.name} className="rounded-xl bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] overflow-hidden">
                  <button onClick={() => setExpandedAgent(isExpanded ? null : agent.name)} className="w-full flex items-center gap-3 p-3 hover:bg-white/[0.02] transition-colors">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15`, color }}><Icon size={16} /></div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-[var(--nexus-text-primary)]">{agent.name}</span>
                        <span className="text-[10px] text-[var(--nexus-text-muted)]">{agent.description}</span>
                      </div>
                      <p className="text-[10px] text-[var(--nexus-text-muted)] mt-0.5">{agent.desc}</p>
                    </div>
                    <ChevronDown size={14} className={`text-[var(--nexus-text-muted)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  {isExpanded && (
                    <div className="p-3 border-t border-[var(--nexus-border)] animate-nexus-slide-in">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <h4 className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium mb-1">Graph Nodes ({agent.nodes?.length})</h4>
                          <div className="flex flex-wrap gap-1">
                            {(agent.nodes || []).map((node, i) => (
                              <span key={i} className="font-mono text-[10px] px-2 py-0.5 rounded bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-[var(--nexus-text-secondary)]">{node}</span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium mb-1">Key Features</h4>
                          <ul className="space-y-0.5">
                            {(agent.key_features || []).map((f, i) => (
                              <li key={i} className="text-[11px] text-[var(--nexus-text-secondary)] flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full" style={{ backgroundColor: color }} />{f}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
